import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { getErrorMessage } from '../utils/errorUtils';
import { formatRut, formatDate, AID_TYPES } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  HeartHandshake,
  Plus,
  Search,
  Loader2,
  User,
  Package,
  Trash2,
  AlertCircle,
  Calendar,
  TrendingUp,
  CalendarDays,
  AlertTriangle,
  Lock,
  Unlock,
  Store,
} from 'lucide-react';

const Attention = () => {
  const { user } = useAuth();
  const isSocialWorker = user?.role === 'social_worker';
  
  const [attentions, setAttentions] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [products, setProducts] = useState([]);
  const [myStats, setMyStats] = useState(null);
  const [myDesk, setMyDesk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deskModalOpen, setDeskModalOpen] = useState(false);

  // Form state
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [deliveryItems, setDeliveryItems] = useState([]);
  const [currentItem, setCurrentItem] = useState({
    product_id: '',
    quantity: 1,
  });

  const fetchData = useCallback(async () => {
    try {
      const promises = [
        api.get('/attentions'),
        api.get('/beneficiaries'),
        api.get('/products'),
        api.get('/attentions/my-stats'),
      ];
      
      if (isSocialWorker) {
        promises.push(api.get('/desks/me').catch(() => ({ data: null })));
      }

      const responses = await Promise.all(promises);
      setAttentions(responses[0].data);
      setBeneficiaries(responses[1].data);
      setProducts(responses[2].data);
      setMyStats(responses[3].data);
      
      if (isSocialWorker && responses[4]) {
        setMyDesk(responses[4].data);
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [isSocialWorker]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleDeskStatus = async () => {
    if (!myDesk) return;
    try {
      const newStatus = !myDesk.is_open;
      const res = await api.patch(`/desks/${myDesk._id}/status?is_open=${newStatus}`);
      setMyDesk(res.data);
      toast.success(newStatus ? 'Caja PAC Abierta' : 'Caja PAC Cerrada');
      setDeskModalOpen(false); // Close modal on success
    } catch (error) {
      toast.error('Error al cambiar el estado de la caja');
    }
  };

  const filteredAttentions = attentions.filter(attention => {
    if (!searchTerm) return true;
    return attention.beneficiary_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
           attention.beneficiary_rut.includes(searchTerm);
  });

  const handleBeneficiarySelect = (beneficiaryId) => {
    const ben = beneficiaries.find(b => b.beneficiary_id === beneficiaryId);
    setSelectedBeneficiary(ben);
  };

  const addItem = () => {
    if (!currentItem.product_id || currentItem.quantity < 1) {
      toast.error('Seleccione un producto y cantidad');
      return;
    }

    const product = products.find(p => p.product_id === currentItem.product_id);
    if (!product) return;

    if (currentItem.quantity > product.quantity) {
      toast.error(`Stock insuficiente. Disponible: ${product.quantity}`);
      return;
    }

    const existing = deliveryItems.find(i => i.product_id === currentItem.product_id);
    if (existing) {
      toast.error('Este producto ya está en la lista');
      return;
    }

    setDeliveryItems([...deliveryItems, {
      product_id: product.product_id,
      product_name: product.name,
      quantity: currentItem.quantity,
      warehouse_id: product.warehouse_id,
      available: product.quantity,
    }]);

    setCurrentItem({ product_id: '', quantity: 1 });
  };

  const removeItem = (productId) => {
    setDeliveryItems(deliveryItems.filter(i => i.product_id !== productId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedBeneficiary) {
      toast.error('Seleccione un beneficiario');
      return;
    }

    if (!reason) {
      toast.error('Seleccione el motivo de atención');
      return;
    }

    setSubmitting(true);
    try {
      const attentionData = {
        beneficiary_id: selectedBeneficiary.beneficiary_id,
        beneficiary_name: selectedBeneficiary.name,
        beneficiary_rut: selectedBeneficiary.rut,
        reason: reason,
        items: deliveryItems.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: item.quantity,
          warehouse_id: item.warehouse_id,
        })),
        notes: notes,
      };

      await api.post('/attentions', attentionData);
      toast.success('Atención registrada exitosamente');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al registrar atención'));
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedBeneficiary(null);
    setReason('');
    setNotes('');
    setDeliveryItems([]);
    setCurrentItem({ product_id: '', quantity: 1 });
  };

  const availableProducts = products.filter(p => p.quantity > 0);

  return (
    <div className="space-y-6" data-testid="attention-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">
            Atención al Cliente
          </h1>
          <p className="text-slate-500 mt-1">
            Registro de atenciones y entregas a beneficiarios
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* BOTON PARA GESTIONAR CAJA PAC */}
          {isSocialWorker && myDesk && (
             <Button 
                variant="outline" 
                className={myDesk.is_open ? "border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100" : "border-rose-200 text-rose-700 bg-rose-50 hover:bg-rose-100"}
                onClick={() => setDeskModalOpen(true)}
             >
                <Store className="w-4 h-4 mr-2" />
                {myDesk.is_open ? "Caja Abierta" : "Caja Cerrada"}
             </Button>
          )}

          <Dialog open={dialogOpen} onOpenChange={(open) => {
            if (open && isSocialWorker && (!myDesk || !myDesk.is_open)) {
              toast.error("Debe abrir su Caja PAC para poder registrar atenciones");
              return;
            }
            setDialogOpen(open);
          }}>
            <DialogTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-700 shadow-lg shadow-teal-600/20" data-testid="new-attention-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nueva Atención
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">Registrar Nueva Atención</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              {/* Beneficiario */}
              <div className="space-y-2">
                <Label>Beneficiario *</Label>
                <Select onValueChange={handleBeneficiarySelect}>
                  <SelectTrigger className="bg-slate-50" data-testid="attention-beneficiary-select">
                    <SelectValue placeholder="Buscar beneficiario..." />
                  </SelectTrigger>
                  <SelectContent>
                    {beneficiaries.map(ben => (
                      <SelectItem key={ben.beneficiary_id} value={ben.beneficiary_id}>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          <span>{ben.name}</span>
                          <span className="text-slate-400">({formatRut(ben.rut)})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedBeneficiary && (
                  <Card className="bg-slate-50 border-slate-200">
                    <CardContent className="p-4">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500">Nombre</p>
                          <p className="font-medium">{selectedBeneficiary.name}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">RUT</p>
                          <p className="font-medium">{formatRut(selectedBeneficiary.rut)}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Dirección</p>
                          <p className="font-medium">{selectedBeneficiary.address}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Tramo RSH</p>
                          <p className="font-medium">{selectedBeneficiary.rsh_tramo || 'No registrado'}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Motivo de Atención */}
              <div className="space-y-2">
                <Label>Motivo de Atención *</Label>
                <Select value={reason} onValueChange={setReason}>
                  <SelectTrigger className="bg-slate-50" data-testid="attention-reason-select">
                    <SelectValue placeholder="Seleccionar motivo" />
                  </SelectTrigger>
                  <SelectContent>
                    {AID_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                    <SelectItem value="Consulta">Consulta</SelectItem>
                    <SelectItem value="Orientación">Orientación</SelectItem>
                    <SelectItem value="Derivación">Derivación</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Productos a Entregar (Opcional) */}
              <div className="space-y-4">
                <Label>Productos a Entregar (Opcional)</Label>
                <div className="flex gap-2">
                  <Select 
                    value={currentItem.product_id} 
                    onValueChange={(v) => setCurrentItem(prev => ({ ...prev, product_id: v }))}
                  >
                    <SelectTrigger className="flex-1 bg-slate-50" data-testid="attention-product-select">
                      <SelectValue placeholder="Seleccionar producto" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableProducts.map(prod => (
                        <SelectItem key={prod.product_id} value={prod.product_id}>
                          <div className="flex items-center justify-between w-full gap-4">
                            <span>{prod.name}</span>
                            <Badge variant="outline" className="ml-2">
                              Stock: {prod.quantity}
                            </Badge>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    min="1"
                    value={currentItem.quantity}
                    onChange={(e) => setCurrentItem(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                    className="w-24 bg-slate-50"
                    data-testid="attention-quantity-input"
                  />
                  <Button type="button" onClick={addItem} variant="outline" data-testid="attention-add-item-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {/* Items List */}
                {deliveryItems.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Producto</TableHead>
                          <TableHead>Cantidad</TableHead>
                          <TableHead className="text-right">Acción</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {deliveryItems.map((item) => (
                          <TableRow key={item.product_id}>
                            <TableCell className="font-medium">{item.product_name}</TableCell>
                            <TableCell>{item.quantity}</TableCell>
                            <TableCell className="text-right">
                              <Button 
                                type="button" 
                                variant="ghost" 
                                size="sm"
                                onClick={() => removeItem(item.product_id)}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>

              {/* Notas */}
              <div className="space-y-2">
                <Label>Observaciones</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Notas adicionales sobre la atención..."
                  className="bg-slate-50"
                />
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="bg-teal-600 hover:bg-teal-700"
                  disabled={submitting}
                  data-testid="submit-attention-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Registrando...
                    </>
                  ) : (
                    <>
                      <HeartHandshake className="w-4 h-4 mr-2" />
                      Registrar Atención
                    </>
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Social Worker Desk Widget from Dashboard, repeated here for convenience */}
      {isSocialWorker && myDesk && !myDesk.is_open && (
        <Card className="border-l-4 border-l-amber-500 bg-amber-50">
          <CardContent className="p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3 text-amber-800">
              <AlertTriangle className="w-6 h-6" />
              <div>
                <p className="font-semibold">Módulo PAC Cerrado</p>
                <p className="text-sm">Debe abrir su Caja PAC para poder registrar nuevas atenciones.</p>
              </div>
            </div>
            <Button 
              onClick={toggleDeskStatus} 
              className="bg-emerald-600 hover:bg-emerald-700 text-white flex-shrink-0"
            >
              <Unlock className="w-4 h-4 mr-2" /> Abrir Caja PAC
            </Button>
          </CardContent>
        </Card>
      )}

      {isSocialWorker && myDesk && myDesk.is_open && (
        <Card className="border-l-4 border-l-emerald-500 bg-emerald-50">
          <CardContent className="p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3 text-emerald-800">
              <HeartHandshake className="w-5 h-5" />
              <div>
                <p className="font-semibold">Módulo PAC Abierto ({myDesk.name})</p>
                <p className="text-xs">Atendiendo actualmente.</p>
              </div>
            </div>
            <Button 
              variant="outline"
              size="sm"
              onClick={toggleDeskStatus} 
              className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200"
            >
              <Lock className="w-4 h-4 mr-2" /> Cerrar Caja
            </Button>
          </CardContent>
        </Card>
      )}

      {isSocialWorker && !myDesk && !loading && (
        <Card className="border-l-4 border-l-amber-500 bg-amber-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 text-amber-800">
              <AlertTriangle className="w-6 h-6" />
              <div>
                <p className="font-semibold">Sin Caja PAC asignada</p>
                <p className="text-sm">Contacte a su administrador para que le asigne una caja de atención.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* My Stats */}
      {myStats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-teal-50 text-teal-600">
                <HeartHandshake className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Atenciones Hoy</p>
                <p className="text-2xl font-bold text-slate-900">{myStats.today}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-50 text-blue-600">
                <Calendar className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Atenciones del Mes</p>
                <p className="text-2xl font-bold text-slate-900">{myStats.this_month}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-50 text-amber-600">
                <CalendarDays className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Atenciones del Año</p>
                <p className="text-2xl font-bold text-slate-900">{myStats.this_year}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-50 text-emerald-600">
                <TrendingUp className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Histórico</p>
                <p className="text-2xl font-bold text-slate-900">{myStats.total}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar por nombre o RUT del beneficiario..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-50"
              data-testid="search-attentions"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
            </div>
          ) : filteredAttentions.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
              <HeartHandshake className="w-12 h-12 mb-4" />
              <p className="text-lg font-medium">No hay atenciones registradas</p>
              <p className="text-sm">Registre la primera atención para comenzar</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Beneficiario</TableHead>
                    <TableHead className="hidden md:table-cell">Motivo</TableHead>
                    <TableHead className="hidden lg:table-cell">Productos</TableHead>
                    <TableHead className="hidden lg:table-cell">Atendido por</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAttentions.map((attention) => (
                    <TableRow key={attention.attention_id} className="table-row-hover">
                      <TableCell>{formatDate(attention.created_at)}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">{attention.beneficiary_name}</p>
                          <p className="text-xs text-slate-500">{formatRut(attention.beneficiary_rut)}</p>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">{attention.reason}</TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {attention.items && attention.items.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {attention.items.slice(0, 2).map((item, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {item.product_name} x{item.quantity}
                              </Badge>
                            ))}
                            {attention.items.length > 2 && (
                              <Badge variant="outline" className="text-xs">
                                +{attention.items.length - 2}
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-sm">Sin productos</span>
                        )}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">{attention.attended_by_name}</TableCell>
                      <TableCell>
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                          Completada
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Desk Management Modal */}
      {isSocialWorker && myDesk && (
        <Dialog open={deskModalOpen} onOpenChange={setDeskModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Gestión de Caja PAC</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center justify-center p-6 space-y-6">
              <div className={`w-24 h-24 rounded-full flex items-center justify-center ${myDesk.is_open ? 'bg-emerald-100' : 'bg-rose-100'}`}>
                {myDesk.is_open ? (
                  <Unlock className="w-12 h-12 text-emerald-600" />
                ) : (
                  <Lock className="w-12 h-12 text-rose-600" />
                )}
              </div>
              
              <div className="text-center space-y-2">
                <h3 className="text-xl font-bold text-slate-900">
                  {myDesk.name}
                </h3>
                <p className={`font-medium ${myDesk.is_open ? 'text-emerald-600' : 'text-rose-600'}`}>
                  Estado: {myDesk.is_open ? 'Abierta' : 'Cerrada'}
                </p>
                <p className="text-sm text-slate-500">
                  {myDesk.is_open 
                    ? 'Su caja está abierta y lista para registrar atenciones.' 
                    : 'Debe abrir su caja para poder registrar nuevas atenciones en el sistema.'}
                </p>
              </div>

              <Button 
                onClick={toggleDeskStatus}
                className={`w-full py-6 text-lg ${
                  myDesk.is_open 
                    ? 'bg-rose-600 hover:bg-rose-700 text-white' 
                    : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                }`}
              >
                {myDesk.is_open ? (
                  <>
                    <Lock className="w-5 h-5 mr-2" />
                    Cerrar Caja
                  </>
                ) : (
                  <>
                    <Unlock className="w-5 h-5 mr-2" />
                    Abrir Caja
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

    </div>
  );
};

export default Attention;
